// TimeTrackingDashboard.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Divider,
    Tabs,
    Tab,
    Button,
    LinearProgress
} from '@mui/material';
import {
    DateRange as DateRangeIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`time-tracking-tabpanel-${index}`}
            aria-labelledby={`time-tracking-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function TimeTrackingDashboard({ userId, orgId }) {
    const [timeData, setTimeData] = useState({
        dailyHours: [],
        weeklyHours: [],
        monthlyHours: [],
        currentWeekTotal: 0,
        currentMonthTotal: 0,
        weeklyTarget: 40, // Default weekly target (hours)
        monthlyTarget: 160, // Default monthly target (hours)
        averageDailyHours: 0,
        totalDays: 0,
        daysMissedClockOut: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState(0); // 0: Week, 1: Month, 2: Quarter

    const handleTimeRangeChange = (event, newValue) => {
        setTimeRange(newValue);
    };

    const handleRefresh = () => {
        fetchTimeData();
    };

    const fetchTimeData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Calculate date ranges
            const today = new Date();

            // Start of current week (Sunday)
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            startOfWeek.setHours(0, 0, 0, 0);

            // Start of current month
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

            // Start of current quarter
            const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
            const startOfQuarter = new Date(today.getFullYear(), quarterStartMonth, 1);

            // Create Firestore timestamps
            const weekTimestamp = Timestamp.fromDate(startOfWeek);
            const monthTimestamp = Timestamp.fromDate(startOfMonth);
            const quarterTimestamp = Timestamp.fromDate(startOfQuarter);

            // Fetch time logs for the quarter (to cover all potential ranges)
            const logsRef = collection(db, "organizations", orgId, "timeLogs");
            const q = query(
                logsRef,
                where("userId", "==", userId),
                where("timestamp", ">=", quarterTimestamp),
                orderBy("timestamp", "asc")
            );

            const snapshot = await getDocs(q);
            const timeLogs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate() || null
            }));

            // Process time logs
            const dailyHoursMap = new Map();
            const dayMap = new Map();
            let totalHoursWorked = 0;
            let daysMissedClockOut = 0;

            // Group logs by day
            timeLogs.forEach(log => {
                if (!log.timestamp) return;

                const date = log.timestamp.toDateString();

                if (!dayMap.has(date)) {
                    dayMap.set(date, {
                        date: log.timestamp,
                        clockIns: [],
                        clockOuts: []
                    });
                }

                if (log.type === "clockIn") {
                    dayMap.get(date).clockIns.push(log);
                } else if (log.type === "clockOut") {
                    dayMap.get(date).clockOuts.push(log);
                }
            });

            // Calculate hours worked per day
            dayMap.forEach((dayData, dateString) => {
                const { clockIns, clockOuts, date } = dayData;

                if (clockIns.length > 0) {
                    // Check if there are matching clock outs
                    if (clockOuts.length > 0) {
                        // Sort by timestamp
                        clockIns.sort((a, b) => a.timestamp - b.timestamp);
                        clockOuts.sort((a, b) => a.timestamp - b.timestamp);

                        // Calculate hours based on first clock in and last clock out
                        const firstClockIn = clockIns[0].timestamp;
                        const lastClockOut = clockOuts[clockOuts.length - 1].timestamp;
                        const hoursWorked = (lastClockOut - firstClockIn) / (1000 * 60 * 60);

                        // Store in daily hours map
                        dailyHoursMap.set(dateString, {
                            date,
                            hours: hoursWorked,
                            formattedDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        });

                        totalHoursWorked += hoursWorked;
                    } else {
                        // Clock in without clock out
                        daysMissedClockOut++;

                        // Estimate hours based on average workday (8 hours)
                        dailyHoursMap.set(dateString, {
                            date,
                            hours: 0, // Mark as 0 since we don't have actual data
                            formattedDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                            missingClockOut: true
                        });
                    }
                }
            });

            // Create data arrays for charts
            const dailyHours = Array.from(dailyHoursMap.values())
                .sort((a, b) => a.date - b.date);

            // Calculate weekly data
            const weeklyHoursMap = new Map();
            dailyHours.forEach(day => {
                const weekStartDate = new Date(day.date);
                weekStartDate.setDate(weekStartDate.getDate() - weekStartDate.getDay()); // Get Sunday
                const weekKey = weekStartDate.toISOString().split('T')[0];

                if (!weeklyHoursMap.has(weekKey)) {
                    weeklyHoursMap.set(weekKey, {
                        weekStart: weekStartDate,
                        weekEnd: new Date(weekStartDate),
                        hours: 0,
                        formattedDate: `${weekStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                    });
                    // Set end date to Saturday
                    weeklyHoursMap.get(weekKey).weekEnd.setDate(weekStartDate.getDate() + 6);
                }

                if (!day.missingClockOut) {
                    weeklyHoursMap.get(weekKey).hours += day.hours;
                }
            });

            const weeklyHours = Array.from(weeklyHoursMap.values())
                .sort((a, b) => a.weekStart - b.weekStart)
                .map(week => ({
                    ...week,
                    formattedDate: `${week.weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${week.weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                }));

            // Calculate monthly data
            const monthlyHoursMap = new Map();
            dailyHours.forEach(day => {
                const monthKey = `${day.date.getFullYear()}-${day.date.getMonth() + 1}`;

                if (!monthlyHoursMap.has(monthKey)) {
                    monthlyHoursMap.set(monthKey, {
                        month: new Date(day.date.getFullYear(), day.date.getMonth(), 1),
                        hours: 0,
                        formattedDate: day.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                    });
                }

                if (!day.missingClockOut) {
                    monthlyHoursMap.get(monthKey).hours += day.hours;
                }
            });

            const monthlyHours = Array.from(monthlyHoursMap.values())
                .sort((a, b) => a.month - b.month);

            // Calculate current week and month totals
            const currentWeekKey = startOfWeek.toISOString().split('T')[0];
            const currentMonthKey = `${today.getFullYear()}-${today.getMonth() + 1}`;

            const currentWeekTotal = weeklyHoursMap.has(currentWeekKey)
                ? weeklyHoursMap.get(currentWeekKey).hours
                : 0;

            const currentMonthTotal = monthlyHoursMap.has(currentMonthKey)
                ? monthlyHoursMap.get(currentMonthKey).hours
                : 0;

            // Calculate average daily hours
            const totalWorkDays = dailyHours.filter(day => !day.missingClockOut).length;
            const averageDailyHours = totalWorkDays > 0
                ? totalHoursWorked / totalWorkDays
                : 0;

            setTimeData({
                dailyHours,
                weeklyHours,
                monthlyHours,
                currentWeekTotal,
                currentMonthTotal,
                weeklyTarget: 40, // Default weekly target
                monthlyTarget: 160, // Default monthly target
                averageDailyHours,
                totalDays: totalWorkDays,
                daysMissedClockOut
            });

        } catch (err) {
            console.error("Error fetching time data:", err);
            setError("Failed to load your time tracking data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTimeData();
    }, [userId, orgId]);

    // Helper function to format hours
    const formatHours = (hours) => {
        return hours.toFixed(1);
    };

    // Calculate progress percentage
    const getProgressPercentage = (current, target) => {
        return Math.min(100, Math.round((current / target) * 100));
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                    Time Tracking Dashboard
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                >
                    Refresh
                </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph>
                Track your working hours and progress toward targets.
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{ height: '100%', boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Weekly Hours
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                {formatHours(timeData.currentWeekTotal)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                of {timeData.weeklyTarget} hour target
                            </Typography>
                            <LinearProgressWithLabel
                                value={getProgressPercentage(timeData.currentWeekTotal, timeData.weeklyTarget)}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{ height: '100%', boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Monthly Hours
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                {formatHours(timeData.currentMonthTotal)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                of {timeData.monthlyTarget} hour target
                            </Typography>
                            <LinearProgressWithLabel
                                value={getProgressPercentage(timeData.currentMonthTotal, timeData.monthlyTarget)}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{ height: '100%', boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Daily Average
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                {formatHours(timeData.averageDailyHours)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                hours per day
                            </Typography>
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="body2">
                                    Based on {timeData.totalDays} work days
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                    <Card sx={{ height: '100%', boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom>
                                Work Days
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                {timeData.totalDays}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                days tracked this month
                            </Typography>
                            <Box sx={{ mt: 1.5 }}>
                                {timeData.daysMissedClockOut > 0 && (
                                    <Typography variant="body2" color="error">
                                        {timeData.daysMissedClockOut} day(s) with missing clock-out
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Chart Section */}
            <Paper sx={{ p: 3, boxShadow: 2 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={timeRange}
                        onChange={handleTimeRangeChange}
                        variant="fullWidth"
                    >
                        <Tab label="Weekly View" icon={<DateRangeIcon />} iconPosition="start" />
                        <Tab label="Monthly View" icon={<DateRangeIcon />} iconPosition="start" />
                        <Tab label="Daily Details" icon={<DateRangeIcon />} iconPosition="start" />
                    </Tabs>
                </Box>

                <TabPanel value={timeRange} index={0}>
                    <Typography variant="subtitle1" gutterBottom>
                        Weekly Hours Breakdown
                    </Typography>

                    <Box sx={{ height: 400, mt: 3 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={timeData.weeklyHours.slice(-12)} // Show last 12 weeks
                                margin={{ top: 5, right: 30, left: 20, bottom: 80 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="formattedDate"
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis
                                    label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
                                />
                                <Tooltip
                                    formatter={(value) => [`${value.toFixed(1)} hours`, 'Hours Worked']}
                                    labelFormatter={(label) => `Week of ${label}`}
                                />
                                <Legend />
                                <Bar
                                    dataKey="hours"
                                    name="Hours Worked"
                                    fill="#2196F3"
                                />
                                {/* Show weekly target as a horizontal reference line */}
                                <ReferenceLine
                                    y={timeData.weeklyTarget}
                                    stroke="#FF5722"
                                    strokeDasharray="3 3"
                                    label={{ value: 'Target', position: 'insideTopRight' }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </TabPanel>

                <TabPanel value={timeRange} index={1}>
                    <Typography variant="subtitle1" gutterBottom>
                        Monthly Hours Breakdown
                    </Typography>

                    <Box sx={{ height: 400, mt: 3 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={timeData.monthlyHours}
                                margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="formattedDate"
                                    angle={-30}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis
                                    label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
                                />
                                <Tooltip
                                    formatter={(value) => [`${value.toFixed(1)} hours`, 'Hours Worked']}
                                />
                                <Legend />
                                <Bar
                                    dataKey="hours"
                                    name="Hours Worked"
                                    fill="#4CAF50"
                                />
                                {/* Show monthly target as a horizontal reference line */}
                                <ReferenceLine
                                    y={timeData.monthlyTarget}
                                    stroke="#FF5722"
                                    strokeDasharray="3 3"
                                    label={{ value: 'Target', position: 'insideTopRight' }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </TabPanel>

                <TabPanel value={timeRange} index={2}>
                    <Typography variant="subtitle1" gutterBottom>
                        Daily Hours Breakdown
                    </Typography>

                    <Box sx={{ height: 400, mt: 3 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={timeData.dailyHours.slice(-30)} // Show last 30 days
                                margin={{ top: 5, right: 30, left: 20, bottom: 80 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="formattedDate"
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis
                                    label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
                                />
                                <Tooltip
                                    formatter={(value, name, props) => {
                                        if (props.payload.missingClockOut) {
                                            return ["Missing clock-out", "Hours"];
                                        }
                                        return [`${value.toFixed(1)} hours`, "Hours"];
                                    }}
                                    labelFormatter={(label) => `Date: ${label}`}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="hours"
                                    name="Hours Worked"
                                    stroke="#8884d8"
                                    activeDot={{ r: 8 }}
                                />
                                {/* Show daily target as a horizontal reference line */}
                                <ReferenceLine
                                    y={8}
                                    stroke="#FF5722"
                                    strokeDasharray="3 3"
                                    label={{ value: 'Target', position: 'insideTopRight' }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </TabPanel>
            </Paper>
        </Box>
    );
}

// Custom component for linear progress with label
function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress
                    variant="determinate"
                    sx={{ height: 8, borderRadius: 1 }}
                    {...props}
                />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}

// Custom component for reference line in charts
function ReferenceLine({ y, stroke, strokeDasharray, label }) {
    return (
        <g>
            <line
                x1="0%"
                x2="100%"
                y1={y}
                y2={y}
                stroke={stroke}
                strokeDasharray={strokeDasharray}
            />
            {label && (
                <text
                    x="95%"
                    y={y - 5}
                    fill={stroke}
                    textAnchor="end"
                    dominantBaseline="bottom"
                    style={{ fontSize: '12px' }}
                >
                    {label.value}
                </text>
            )}
        </g>
    );
}

export default TimeTrackingDashboard;